/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Inject,
  Input,
  OnDestroy,
  Optional,
  Self,
} from '@angular/core';

import { Type, TypeService } from '@gh/core-type';
import { coalesce, isNotNil } from '@gh/core-util';

import { ColDef, GridOptions } from 'ag-grid-community';
import { isNil, noop, omitBy } from 'lodash';

import { GridTemplateCellRendererComponent } from '../../cell-components/renderers/template-cell-renderer/template-cell-renderer.component';
import { GRID_COLUMN_FEATURE, GridColumnFeature } from '../../features/feature.interfaces';
import { DEFAULT_COLUMN_WIDTH, MIN_COLUMN_WIDTH } from '../grid.utils';
import { GridCellTemplateDirective } from './grid-cell-template.directive';

@Component({
  selector: 'gh-grid-column',
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridColumnComponent implements OnDestroy {
  @Input() field: string;
  @Input() width: number;
  @Input() sortable: boolean;
  @Input() cellClass: string | string[] | ((cellClassParams: any) => string | string[]);
  @Input() headerName: string;
  @Input() primary: boolean;
  @Input() suppressSizeToFit: boolean;
  @Input() comparator: any;

  @ContentChild(GridCellTemplateDirective, { static: false }) cellTemplate: Maybe<GridCellTemplateDirective>;

  private _visible?: boolean;
  private _enabled?: boolean;

  private _typeFormatter: Type<any>;
  private _name: string;
  private _onVisibilityChange: ProcedureFn<boolean> = noop;
  private _onEnabled: ProcedureFn<boolean> = noop;

  constructor(@Optional() @Self() @Inject(GRID_COLUMN_FEATURE) private features: Maybe<GridColumnFeature[]>,
              private typeService: TypeService) {

  }

  @Input()
  set name(name: string) {
    this._name = name;
  }

  get name(): string {
    return this._name || this.field;
  }

  /**
   * Only visible columns are displayed in the layout dialog
   * @param visible
   */
  @Input()
  set visible(visible: boolean) {
    if (this._visible !== visible) {
      this._visible = visible;
      this._onVisibilityChange(visible);
    }
  }

  get visible(): boolean {
    return coalesce(this._visible, true);
  }

  /**
   * Only enabled columns are displayed. Disabled columns are always displayed in the layout dialog
   * @param enabled
   */
  @Input()
  set enabled(enabled: boolean) {
    this._enabled = enabled;
    this._onEnabled(enabled);
  }

  get enabled(): boolean {
    return coalesce(this._enabled, true);
  }

  @Input()
  set typeFormatter(name: string) {
    this._typeFormatter = this.typeService.get(name);
  }

  createColDef(): ColDef {
    const cellTemplateRef = this.cellTemplate && this.cellTemplate.templateRef;

    const colDef: ColDef = {
      field: this.field || this._name,
      cellClass: this.cellClass,
      headerClass: this.primary ? 'header-cell--primary' : '',
      cellRendererFramework: cellTemplateRef ? GridTemplateCellRendererComponent : void 0,
      cellRendererParams: cellTemplateRef && { templateRef: cellTemplateRef },
      valueFormatter: this._typeFormatter ? (params) => this._typeFormatter.format(params.value) : void 0,
    };

    if (this.sortable) {
      colDef.sortable = this.sortable;
    }
    if (this.width) {
      colDef.width = this.width || DEFAULT_COLUMN_WIDTH;
    }
    if (this.headerName) {
      colDef.headerName = this.headerName;
    }

    if (isNotNil(this.suppressSizeToFit)) {
      colDef.suppressSizeToFit = this.suppressSizeToFit;
    }

    if (isNotNil(this.comparator)) {
      colDef.comparator = this.comparator;
    }

    if (isNotNil(this.visible)) {
      colDef.hide = !this.visible;
    }

    if (this.visible && isNotNil(this.enabled)) {
      colDef.hide = !this.enabled;
    }

    colDef.minWidth = colDef.minWidth || MIN_COLUMN_WIDTH;

    if (this.features) {
      this.features.forEach((feature) => feature.enhanceColumn(colDef));
    }

    return omitBy(colDef, isNil);
  }

  afterGridOptionsEnhanced(options: GridOptions): void {
    if (this.features) {
      this.features.forEach((feature) => feature.afterGridOptionsEnhanced && feature.afterGridOptionsEnhanced(options));
    }
  }

  ngOnDestroy(): void {
    if (this.features) {
      this.features.forEach((feature) => feature.onDestroy && feature.onDestroy());
    }
  }

  setOnVisibilityChange(callback: ProcedureFn<boolean>): void {
    this._onVisibilityChange = callback;
  }

  setOnEnabled(callback: ProcedureFn<boolean>): void {
    this._onEnabled = callback;
  }
}
